/* eslint-disable */
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

// Session 39 — Discord references fully removed per launch-readiness audit.

const STEPS = [
  {
    key: "welcome",
    eyebrow: "Welcome to Métier",
    h: (name) => <>Welcome, <em>{name.split(" ")[0] || "there"}</em>.</>,
    body: () => (
      <>
        You&apos;re one of the first members of Métier, a curated professional network for people who work anywhere. Help shape what we build next by sharing your feedback.
      </>
    ),
    ctaLabel: "Continue",
  },
  {
    key: "feedback",
    eyebrow: "Your voice shapes this",
    h: () => <>We want your <em>feedback</em>.</>,
    body: () => (
      <>
        Every page has a Support link at the bottom. Use it liberally, bug reports, dumb ideas, requests, praise. A real human reads every message and reply within one business day.
      </>
    ),
    ctaLabel: "Got it",
  },
  {
    key: "founding",
    eyebrow: "Locked in for life",
    h: () => <>You&apos;re a <em>Founding Member</em>.</>,
    body: () => (
      <>
        Every early member earns permanent Founding status. That badge stays on your profile forever, a small thank-you for being one of the first to help build Métier.
      </>
    ),
    ctaLabel: "Enter Métier →",
  },
];

export default function WelcomeModal() {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  // Session 34.2 — persistent dismiss. "Take me in, I'll finish later"
  // now writes to localStorage so the modal stays dismissed across sessions
  // (previously sessionStorage-only, which meant the modal blocked
  // community-feed interactions on every fresh visit — the "can't edit a
  // post" bug reported in production).
  useEffect(() => {
    if (user && !user.onboarded_at) {
      const key = `metier_onboarding_snooze_${user.user_id}`;
      if (localStorage.getItem(key) === "1" || sessionStorage.getItem(key) === "1") {
        setDismissed(true);
      }
    }
  }, [user]);

  // ESC key dismisses the modal (accessibility + escape-hatch)
  useEffect(() => {
    if (!user || user.onboarded_at || dismissed) return;
    const onKey = (e) => { if (e.key === "Escape") snooze(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dismissed]);

  if (!user) return null;
  if (user.onboarded_at) return null;
  if (dismissed) return null;

  const STEPS_ACTIVE = STEPS;

  const s = STEPS_ACTIVE[step];
  const isLast = step === STEPS_ACTIVE.length - 1;

  const complete = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/complete");
      await refresh();
    } catch { /* ignore, user still moves on locally */ }
    setSaving(false);
    setDismissed(true);
  };

  const next = () => {
    if (isLast) return complete();
    setStep(step + 1);
  };
  const snooze = () => {
    // Persist across sessions so the tour never blocks the app again.
    // Users can re-open the tour via their profile menu (future enhancement).
    localStorage.setItem(`metier_onboarding_snooze_${user.user_id}`, "1");
    setDismissed(true);
  };

  // Clicks on the backdrop (outside the card) should snooze — matches
  // standard modal UX and prevents accidental "stuck" state.
  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) snooze();
  };

  return (
    <div className="m-welcome-backdrop" data-testid="welcome-modal-backdrop"
         role="dialog" aria-modal="true" onClick={onBackdropClick}>
      <div className="m-welcome-card" data-testid="welcome-modal">
        <button
          type="button"
          className="m-welcome-close"
          onClick={snooze}
          aria-label="Dismiss for now"
          data-testid="welcome-modal-close"
        >×</button>

        <div className="m-welcome-progress" data-testid="welcome-modal-progress">
          {STEPS_ACTIVE.map((_, i) => (
            <span
              key={i}
              className={`m-welcome-progress-dot ${i <= step ? "on" : ""}`}
              aria-hidden="true"
            />
          ))}
        </div>

        <span className="m-eyebrow m-welcome-eyebrow" dangerouslySetInnerHTML={{ __html: s.eyebrow }} />
        <h2 className="m-welcome-h" data-testid={`welcome-modal-h-${s.key}`}>
          {s.h(user.name || "")}
        </h2>
        <p className="m-welcome-body" data-testid={`welcome-modal-body-${s.key}`}>
          {s.body()}
        </p>

        {s.key === "founding" && (
          <div className="m-welcome-founding-strip" data-testid="welcome-founding-strip">
            <span className="m-badge-founding">Founding Member</span>
          </div>
        )}

        <div className="m-welcome-actions">
          {s.external ? (
            <a
              href={s.external}
              target="_blank"
              rel="noopener noreferrer"
              className="m-btn-gold"
              onClick={() => setStep(step + 1)}
              data-testid={`welcome-modal-cta-${s.key}`}
            >
              {s.ctaLabel} →
            </a>
          ) : (
            <button
              type="button"
              className="m-btn-gold"
              onClick={next}
              disabled={saving}
              data-testid={`welcome-modal-cta-${s.key}`}
            >
              {saving ? "One moment…" : s.ctaLabel}
            </button>
          )}
          {s.secondaryLabel && (
            <button
              type="button"
              className="m-btn-ghost"
              onClick={() => setStep(step + 1)}
              data-testid={`welcome-modal-skip-${s.key}`}
            >
              {s.secondaryLabel}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={snooze}
          className="m-welcome-later"
          data-testid="welcome-modal-later"
        >Take me in, I&apos;ll finish this tour later</button>
      </div>
    </div>
  );
}
